import React, { useState, useRef, useMemo } from "react";
import {
  Bell,
  GripVertical,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  ChevronRight as ChevronExpand,
  ChevronDown as ChevronCollapse,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import AlertRuleModal from "./AlertRuleModal";
import { useAlertRules } from "../../../hooks/useAlertRules";
import { useClientFilter } from "../../../hooks/useClientFilter";

export default function AlertRules() {
  // Use the custom hook for API integration
  const {
    rules: allAlertRules,
    conditions,
    workspaces,
    isLoading,
    isConditionsLoading,
    isWorkspacesLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isSaving,
    error,
    createRule,
    updateRule,
    deleteRule,
    toggleRule,
    reorderRules,
    moveRuleUp,
    moveRuleDown,
    refetch,
    resetStatus,
  } = useAlertRules();

  // Use client filter hook for filtering rules by selected client
  const { filterByClient } = useClientFilter();

  // Filter alert rules by selected client
  const alertRules = useMemo(() => {
    return filterByClient(allAlertRules, 'clientUUID');
  }, [allAlertRules, filterByClient]);

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [expandedRules, setExpandedRules] = useState({});
  const [allExpanded, setAllExpanded] = useState(true);
  const dragNodeRef = useRef(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' or 'edit'
  const [selectedRule, setSelectedRule] = useState(null);

  // Delete confirmation state
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Initialize expanded state when rules change
  React.useEffect(() => {
    if (alertRules.length > 0) {
      setExpandedRules((prev) => {
        const newState = { ...prev };
        alertRules.forEach((rule) => {
          if (!(rule.id in newState)) {
            newState[rule.id] = true;
          }
        });
        return newState;
      });
    }
  }, [alertRules]);

  // Toggle individual rule expansion
  const toggleRuleExpansion = (id) => {
    setExpandedRules((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Collapse/Expand all rules
  const toggleAllExpansion = () => {
    const newState = !allExpanded;
    setAllExpanded(newState);
    setExpandedRules(
      alertRules.reduce((acc, rule) => ({ ...acc, [rule.id]: newState }), {})
    );
  };

  // Handle toggle change with API call
  const handleToggle = async (id) => {
    const rule = alertRules.find((r) => r.id === id);
    if (rule) {
      await toggleRule(id, !rule.enabled);
    }
  };

  // Drag handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    dragNodeRef.current = e.target;

    setTimeout(() => {
      if (dragNodeRef.current) {
        dragNodeRef.current.classList.add("alert-rule--dragging");
      }
    }, 0);

    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.outerHTML);
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragLeave = (e) => {
    const relatedTarget = e.relatedTarget;
    if (!e.currentTarget.contains(relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newRules = [...alertRules];
    const draggedRule = newRules[draggedIndex];

    newRules.splice(draggedIndex, 1);
    newRules.splice(dropIndex, 0, draggedRule);

    // Call API to persist reorder
    await reorderRules(newRules);

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    if (dragNodeRef.current) {
      dragNodeRef.current.classList.remove("alert-rule--dragging");
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
    dragNodeRef.current = null;
  };

  // Modal handlers
  const handleAddNewRule = () => {
    setModalMode("add");
    setSelectedRule(null);
    setIsModalOpen(true);
  };

  const handleEditRule = (rule) => {
    setModalMode("edit");
    setSelectedRule(rule);
    setIsModalOpen(true);
  };

  const handleSaveRule = async (ruleData) => {
    let result;
    
    if (modalMode === "add") {
      result = await createRule({
        name: ruleData.name,
        description: ruleData.description,
        enabled: ruleData.enabled,
        conditions: ruleData.conditions,
        emails: ruleData.emails,
        workspaceIds: ruleData.workspaceIds,
      });
    } else {
      result = await updateRule(ruleData.id, {
        name: ruleData.name,
        description: ruleData.description,
        enabled: ruleData.enabled,
        conditions: ruleData.conditions,
        emails: ruleData.emails,
        workspaceIds: ruleData.workspaceIds,
      });
    }

    if (result.success) {
      setIsModalOpen(false);
      setSelectedRule(null);
      // Expand the new rule if created
      if (modalMode === "add" && result.data?.id) {
        setExpandedRules((prev) => ({ ...prev, [result.data.id]: true }));
      }
    }

    return result;
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRule(null);
    resetStatus();
  };

  // Delete handlers
  const handleDeleteClick = (ruleId) => {
    setDeleteConfirmId(ruleId);
  };

  const handleConfirmDelete = async (ruleId) => {
    const result = await deleteRule(ruleId);
    if (result.success) {
      setDeleteConfirmId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmId(null);
  };

  // Check if any rules are expanded
  const someExpanded = Object.values(expandedRules).some((v) => v);

  // Loading state
  if (isLoading) {
    return (
      <div className="alert-rules">
        <div className="alert-rules__header">
          <div className="alert-rules__title-section">
            <Bell size={20} />
            <h3 className="alert-rules__title">Alert Rules</h3>
          </div>
        </div>
        <div className="alert-rules__loading">
          <Loader2 size={32} className="alert-rules__spinner" />
          <p>Loading alert rules...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="alert-rules">
        <div className="alert-rules__header">
          <div className="alert-rules__title-section">
            <Bell size={20} />
            <h3 className="alert-rules__title">Alert Rules</h3>
          </div>
        </div>
        <div className="alert-rules__error">
          <AlertCircle size={32} />
          <p>{error}</p>
          <button className="btn btn--primary" onClick={refetch}>
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="alert-rules">
      <div className="alert-rules__header">
        <div className="alert-rules__title-section">
          <Bell size={20} />
          <h3 className="alert-rules__title">Alert Rules</h3>
        </div>
        <div className="alert-rules__actions">
          <button className="tdt-toolbar-btn" onClick={toggleAllExpansion}>
            {someExpanded ? (
              <>
                <ChevronCollapse size={16} />
                <span>Collapse All</span>
              </>
            ) : (
              <>
                <ChevronExpand size={16} />
                <span>Expand All</span>
              </>
            )}
          </button>
          <button className="btn btn--primary" onClick={handleAddNewRule}>
            Add New Rule
          </button>
        </div>
      </div>

      <p className="alert-rules__hint">
        Drag and drop rules or use the arrows to set priority order. Rules are
        evaluated top to bottom.
      </p>

      {alertRules.length === 0 ? (
        <div className="alert-rules__empty">
          <Bell size={48} />
          <p>No alert rules configured yet.</p>
          <button className="btn btn--primary" onClick={handleAddNewRule}>
            Create Your First Rule
          </button>
        </div>
      ) : (
        <div className="alert-rules__list">
          {alertRules.map((rule, index) => (
            <div
              key={rule.id}
              className={`alert-rule ${
                dragOverIndex === index ? "alert-rule--drag-over" : ""
              } ${draggedIndex === index ? "alert-rule--dragging" : ""} ${
                !expandedRules[rule.id] ? "alert-rule--collapsed" : ""
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div className="alert-rule__header">
                <div className="alert-rule__left">
                  {/* Expand/Collapse Toggle */}
                  <button
                    className={`alert-rule__expand-btn ${
                      expandedRules[rule.id]
                        ? "alert-rule__expand-btn--expanded"
                        : ""
                    }`}
                    onClick={() => toggleRuleExpansion(rule.id)}
                    title={expandedRules[rule.id] ? "Collapse" : "Expand"}
                  >
                    <ChevronRight size={16} />
                  </button>

                  {/* Drag Handle */}
                  <div
                    className="alert-rule__drag-handle"
                    title="Drag to reorder"
                  >
                    <GripVertical size={18} />
                  </div>

                  {/* Order Number */}
                  <span className="alert-rule__order">{index + 1}</span>

                  <div className="switch">
                    <input
                      id={`rule-${rule.id}`}
                      type="checkbox"
                      className="switch__input"
                      checked={rule.enabled}
                      onChange={() => handleToggle(rule.id)}
                    />
                    <label
                      htmlFor={`rule-${rule.id}`}
                      className="switch__label"
                    ></label>
                  </div>

                  <div className="alert-rule__info">
                    <h4 className="alert-rule__name">{rule.name}</h4>
                    <p className="alert-rule__description">{rule.description}</p>
                  </div>
                </div>

                <div className="alert-rule__right">
                  <div
                    className={`chip chip--${
                      rule.enabled ? "success" : "secondary"
                    }`}
                  >
                    {rule.enabled ? "Active" : "Inactive"}
                  </div>

                  {/* Edit Button */}
                  <button
                    className="alert-rule__action-btn"
                    onClick={() => handleEditRule(rule)}
                    title="Edit rule"
                    disabled={isSaving}
                  >
                    <Pencil size={16} />
                  </button>

                  {/* Delete Button */}
                  <button
                    className="alert-rule__action-btn alert-rule__action-btn--danger"
                    onClick={() => handleDeleteClick(rule.id)}
                    title="Delete rule"
                    disabled={isDeleting}
                  >
                    {isDeleting && deleteConfirmId === rule.id ? (
                      <Loader2 size={16} className="alert-rules__spinner" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                  </button>

                  {/* Up/Down Arrows */}
                  <div className="alert-rule__arrows">
                    <button
                      className="alert-rule__arrow-btn"
                      onClick={() => moveRuleUp(index)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <ChevronUp size={16} />
                    </button>
                    <button
                      className="alert-rule__arrow-btn"
                      onClick={() => moveRuleDown(index)}
                      disabled={index === alertRules.length - 1}
                      title="Move down"
                    >
                      <ChevronDown size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Delete Confirmation */}
              {deleteConfirmId === rule.id && (
                <div className="alert-rule__delete-confirm">
                  <p>Are you sure you want to delete this rule?</p>
                  <div className="alert-rule__delete-actions">
                    <button
                      className="btn btn--outline btn--sm"
                      onClick={handleCancelDelete}
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn--danger btn--sm"
                      onClick={() => handleConfirmDelete(rule.id)}
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                        <>
                          <Loader2 size={14} className="alert-rules__spinner" />
                          Deleting...
                        </>
                      ) : (
                        "Delete"
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Collapsible Details Section */}
              <div
                className={`alert-rule__details ${
                  expandedRules[rule.id] ? "alert-rule__details--expanded" : ""
                }`}
              >
                <div className="alert-rule__details-content">
                  <div className="alert-rule__detail">
                    <span className="alert-rule__detail-label">Conditions:</span>
                    <p className="alert-rule__detail-value alert-rule__detail-value--mono">
                      {rule.conditions}
                    </p>
                  </div>
                  <div className="alert-rule__detail">
                    <span className="alert-rule__detail-label">Notify:</span>
                    <p className="alert-rule__detail-value">
                      {rule.emails && rule.emails.length > 0
                        ? rule.emails.join(", ")
                        : "No notifications configured"}
                    </p>
                  </div>
                  {rule.workspaceIds && rule.workspaceIds.length > 0 && (
                    <div className="alert-rule__detail alert-rule__detail--full">
                      <span className="alert-rule__detail-label">Workspaces:</span>
                      <div className="alert-rule__workspace-tags">
                        {rule.workspaces.map((ws) => (
                          <span key={ws.id} className="alert-rule__workspace-tag">
                            {ws.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Alert Rule Modal */}
      <AlertRuleModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveRule}
        rule={selectedRule}
        mode={modalMode}
        conditions={conditions}
        isConditionsLoading={isConditionsLoading}
        workspaces={workspaces}
        isWorkspacesLoading={isWorkspacesLoading}
        isSaving={isSaving}
      />
    </div>
  );
}
